import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PawPrint from '../components/PawPrint';
import { ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/confirm-email', { email, otp });
      toast.success('Email confirmed! You can now login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Email not found. Please go back to signup.');
      return;
    }
    setResendLoading(true);
    try {
      await api.post('/auth/resend-otp', { email });
      toast.success('OTP resent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#1B4332]" data-testid="confirm-email-page">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#BFDB38]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF6B6B]/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-center gap-2 mb-8">
          <PawPrint className="w-10 h-10" color="#BFDB38" />
          <span className="text-3xl font-black text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>PurrCheck</span>
        </div>

        <div className="bg-white border-2 border-[#1B4332]/10 rounded-3xl p-8 shadow-2xl" data-testid="confirm-email-card">
          <h2 className="text-2xl font-black text-[#1B4332] text-center mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Confirm Your Email
          </h2>
          <p className="text-center text-[#1B4332]/50 text-sm mb-6">We sent a 6-digit code to <span className="font-bold text-[#FF6B6B]">{email}</span></p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="otp" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">OTP Code</Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="bg-white border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] text-center text-2xl font-bold tracking-widest h-14"
                maxLength={6}
                required
                data-testid="confirm-otp-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-full font-bold h-12 bg-[#1B4332] hover:bg-[#1B4332]/90 text-white text-base hover:-translate-y-0.5 transition-all"
              disabled={loading || resendLoading}
              data-testid="confirm-submit-btn"
            >
              {loading ? 'Confirming...' : 'Confirm Email'}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleResend}
              className="w-full rounded-full font-bold h-12 border-2 border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 transition-all mt-3"
              disabled={loading || resendLoading}
              data-testid="resend-otp-btn"
            >
              {resendLoading ? 'Resending...' : 'Resend OTP'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#1B4332]/50 mt-6">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-[#FF6B6B] font-bold hover:underline" data-testid="confirm-back-link">
              <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
